// The Economic Value Profile of the EPM scorecard: what each model shown contributes, as the revenue and value it
// creates less the cost of the people and systems doing its work. Revenue is money; where a money value isn't practical
// the value set on outputs is the measure, counted one for one with it. Above the line it creates value, below it destroys
// value. Outputs that are used but have no value reaching them are unrealised value, shown as a cap at what producing
// them costs: the least they are worth, until someone sets their value.
//
// A model below the line is "misaligned" when nothing it makes carries value or is used, so its work serves nothing
// valued, and a "value drag" when it creates value, but less than it costs.
//
// Pure, so it can be tested without a page
import { AnalysisEdge, AnalysisNode } from '../common/analysis-model';
import { NODE_SUPERTYPE } from '../common/linqset-model';

export type ValueProfileLabel = 'Misaligned' | 'Unrealised' | 'Value drag';

export interface ValueProfileBar {
    modelKey: string;
    name: string;
    revenue: number;        // a year
    value: number;          // the value set on its outputs, a year
    created: number;        // revenue and value together
    cost: number;           // of the people and systems doing its work, a year
    net: number;            // created less cost: above the line, or below it
    unrealised: number;     // what producing its used but unvalued outputs costs
    label?: ValueProfileLabel;
}

export interface ValueProfileInput {
    modelKeys: string[];
    modelName: (key: string) => string;
    nodes: AnalysisNode[];
    edges: AnalysisEdge[];
    created: (modelKey: string) => { revenue: number; value: number };
    cost: (modelKey: string) => number;
    inUse: (output: AnalysisNode) => boolean;
}

export const buildValueProfile = (input: ValueProfileInput): ValueProfileBar[] => {
    const byKey = new Map(input.nodes.map(n => [n._key, n]));
    const incoming = new Map<string, string[]>();
    input.edges.forEach(e => (incoming.get(e.to) || incoming.set(e.to, []).get(e.to)!).push(e.from));

    return input.modelKeys.map(modelKey => {
        const outputs = input.nodes.filter(n => n.modelKey === modelKey && n.superType === NODE_SUPERTYPE.INFORMATION);
        // used, but no value or revenue reaches them
        const unvalued = outputs.filter(o => !o.value && !o.revenue && !o.userValue && !o.userRevenue && !o.joinedOut && input.inUse(o));
        // the model's steps producing them, each counted once however many of them it feeds
        const steps = new Set<string>();
        const queue = unvalued.map(o => o._key);
        const seen = new Set(queue);
        while (queue.length) {
            (incoming.get(queue.shift()!) || []).forEach(from => {
                const node = byKey.get(from);
                if (!node || node.modelKey !== modelKey || seen.has(from)) return;
                seen.add(from);
                queue.push(from);
                if (node.superType === NODE_SUPERTYPE.ACTION) steps.add(from);
            });
        }
        const unrealised = [...steps].reduce((t, k) => t + (byKey.get(k)!.cost || 0), 0);
        const { revenue, value } = input.created(modelKey), cost = input.cost(modelKey);
        const created = revenue + value;
        const net = created - cost;
        const anyValue = created > 0 || outputs.some(o => input.inUse(o));
        const label: ValueProfileLabel | undefined = unrealised > 0 ? 'Unrealised' : net < 0 ? (anyValue ? 'Value drag' : 'Misaligned') : undefined;
        return { modelKey, name: input.modelName(modelKey) || 'Unnamed model', revenue, value, created, cost, net, unrealised, label };
    });
};

// how the bars are drawn, as shares of the chart's height: above the line the value created and the unrealised cap on
// it, below it the value destroyed. The line sits where the tallest above and the deepest below fit
export interface ValueProfileColumn {
    bar: ValueProfileBar;
    created: number;        // shares of the chart's height
    unrealised: number;
    destroyed: number;
}

export const valueProfileChart = (bars: ValueProfileBar[]) => {
    const up = Math.max(0, ...bars.map(b => Math.max(0, b.net) + b.unrealised));
    const down = Math.max(0, ...bars.map(b => Math.max(0, -b.net)));
    const scale = up + down || 1;
    return {
        above: up / scale,      // the share of the height above the line
        below: down / scale,
        columns: bars.map(bar => ({
            bar,
            created: Math.max(0, bar.net) / scale,
            unrealised: bar.unrealised / scale,
            destroyed: Math.max(0, -bar.net) / scale,
        }) as ValueProfileColumn),
    };
};
